class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('drawer') === 'open') {
      this.openDrawer();
    }
  }

  openDrawer() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      this.open(cartLink);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const path = window.location.pathname;
    const firstSegment = path.split('/').filter(Boolean)[0];
    const isLocalizedPath = firstSegment && (firstSegment.length === 2 || (firstSegment.length <= 6 && firstSegment.includes('-')));
    if (isLocalizedPath) {
      this._refreshDrawerForLocale(path).then(() => this._openDrawer()).catch(() => this._openDrawer());
    } else {
      this._openDrawer();
    }
  }

  _openDrawer() {
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 0);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  _refreshDrawerForLocale(path) {
    const url = `${path}${path.indexOf('?') >= 0 ? '&' : '?'}section_id=cart-drawer`;
    return fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sourceDrawer = doc.getElementById('CartDrawer');
        const targetDrawer = this.querySelector('#CartDrawer');
        if (sourceDrawer && targetDrawer && sourceDrawer.innerHTML) {
          targetDrawer.innerHTML = sourceDrawer.innerHTML;
        }
      });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Guard: Check if parsedState exists
    if (!parsedState) {
      console.warn('CartDrawer.renderContents: No parsedState provided');
      return;
    }

    // Guard: Check if sections exist
    if (!parsedState.sections) {
      console.warn('CartDrawer.renderContents: No sections in parsedState');
      setTimeout(() => this.open(), 0);
      return;
    }

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    
    this.productId = parsedState.id;
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

      if (!sectionElement) return;
      
      // Guard: Check if this specific section exists in the response
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) {
        console.warn(`CartDrawer.renderContents: Missing section "${section.id}"`);
        return;
      }
      
      const newInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (newInnerHTML) {
        sectionElement.innerHTML = newInnerHTML;
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    
    try {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const element = parsedHtml.querySelector(selector);
      return element ? element.innerHTML : null;
    } catch (error) {
      console.error('Error parsing section HTML:', error);
      return null;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);